// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, Stack, Tab } from '@mui/material';
// Components, Layouts, Pages
import { BaseTabList, BaseToolbar } from '~/components/common';
import { InfoVendorTab, PurchaseOrdersVendorTab, SubrentalsVendorTab } from '~/components/module/vendors';
// Others
import { RoleEnum, VendorTabEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseTabItem } from '~/utils/interface/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './VendorsDetails.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const VendorsDetails = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/vendors', 'common/base']);
  const { hash } = useLocation();
  const navigate = useNavigate();
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: VendorTabEnum.DETAILS, component: <InfoVendorTab /> }];

    items.push({ label: VendorTabEnum.PURCHASE_ORDER, component: <PurchaseOrdersVendorTab /> });

    items.push({ label: VendorTabEnum.SUBRENTALS, component: <SubrentalsVendorTab /> });

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(VendorTabEnum.DETAILS);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.vendors);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.vendors);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.vendors);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.vendors);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.vendors);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.vendors);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.vendors);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.vendors);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.vendors);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='vendorsDetailsPage' className={cx('container')}>
      <BaseToolbar title={t('vendor_detail_page_title')} isBack onBackToPreviousPage={handleOnBack}></BaseToolbar>

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            label={t(`tab_label_${item.label}`)}
            className={cx('tabItem', { active: tabKey === item.label })}
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')}>
        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default VendorsDetails;
